import React, { useState } from 'react';
import './ShareListModal.css';

export default function ShareListModal({ isOpen, listId, listName, sharedUsers = [], onClose, onShare, onUnshare }) {
  const [email, setEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleShare = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSharing(true);
    setError('');
    
    try {
      await onShare(listId, email.trim());
      setEmail('');
    } catch (err) {
      setError(err.message || 'Failed to share list');
    } finally {
      setIsSharing(false);
    }
  };

  const handleUnshare = async (userId) => {
    try {
      await onUnshare(listId, userId);
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Share "{listName}"</h3>
          <button
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleShare} className="share-form">
            <label className="share-form-label">
              Share with someone by email
            </label>
            <div className="share-form-group">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="Enter email address"
                className="share-input"
                disabled={isSharing}
                autoFocus
              />
              <button
                type="submit"
                disabled={isSharing || !email.trim()}
                className="btn-custom btn-primary-custom"
              >
                {isSharing ? 'Sharing...' : 'Share'}
              </button>
            </div>
            {error && (
              <div className="share-error">
                {error}
              </div>
            )}
          </form>

          {sharedUsers && sharedUsers.length > 0 && (
            <div className="shared-users-section">
              <h4 className="shared-users-title">People with access</h4>
              <ul className="shared-users-list">
                {sharedUsers.map((user) => (
                  <li key={user.user_id} className="shared-user-item">
                    <div className="shared-user-info">
                      <div className="shared-user-avatar">
                        {user.user_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="shared-user-details">
                        <div className="shared-user-name">{user.user_name}</div>
                        <div className="shared-user-email">{user.user_email}</div>
                      </div>
                      {user.role === 'owner' && (
                        <span className="shared-user-badge">Owner</span>
                      )}
                    </div>
                    {user.role !== 'owner' && (
                      <button
                        className="btn-remove-user"
                        onClick={() => handleUnshare(user.user_id)}
                        title="Remove access"
                        aria-label={`Remove ${user.user_name}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="share-info">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>All users have full access to add, edit, and delete tasks in this list.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

